import type { CalculatorResults } from '@/lib/calculator'
import {Copy, Martini, Printer, Snowflake, Wine} from 'lucide-react'
import { useState } from 'react'

interface Props {
  results: CalculatorResults
  onPrint: () => void
  onShare: () => void
}

function StatCard({
  icon,
  title,
  primary,
  primaryLabel,
  lines,
}: {
  icon: React.ReactNode
  title: string
  primary: string
  primaryLabel: string
  lines: { label: string, value: string }[]
}) {
  return (
    <div className="rounded-2xl bg-white shadow-xl p-5 border border-[#D4AF37]/15">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#F7EBE8] flex items-center justify-center text-[#6B1D2F]">
          {icon}
        </div>
        <h3 className="font-serif text-lg font-semibold text-[#222222]">{title}</h3>
      </div>
      <div className="mb-2">
        <div className="text-2xl font-bold text-[#6B1D2F]">{primary}</div>
        <div className="text-xs text-gray-500">{primaryLabel}</div>
      </div>
      <ul className="space-y-1 mt-3 border-t border-gray-100 pt-3">
        {lines.map(l => (
          <li key={l.label} className="flex justify-between text-sm">
            <span className="text-gray-500">{l.label}</span>
            <span className="font-medium text-[#222222]">{l.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function CalculatorResultsPanel({ results, onPrint, onShare }: Props) {
  const [copied, setCopied] = useState(false)

  const handleShare = () => {
    onShare()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6" id="results-section">
      {/* Hero KPI */}
      <div className="rounded-2xl bg-gradient-to-br from-[#6B1D2F] to-[#4a1420] shadow-xl p-6 sm:p-8 text-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="text-sm uppercase tracking-wide text-[#D4AF37] font-medium">Total Drinks Needed</div>
            <div className="font-serif text-4xl sm:text-5xl font-bold mt-1">{results.totalDrinks.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-wide text-[#D4AF37] font-medium">Estimated Total Cost</div>
            <div className="font-serif text-4xl sm:text-5xl font-bold mt-1">
              ${results.costLow.toLocaleString()}-${results.costHigh.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          <button
            type="button"
            onClick={onPrint}
            aria-label="Print or export the drink shopping list as PDF"
            className="inline-flex items-center gap-2 rounded-lg bg-white/15 hover:bg-white/25 px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Printer className="w-4 h-4" aria-hidden="true" />
            Print / Export PDF Shopping List
          </button>
          <button
            type="button"
            onClick={handleShare}
            aria-label="Copy a shareable link with your calculator inputs"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D4AF37] hover:bg-[#c29d2c] text-[#222222] px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Copy className="w-4 h-4" aria-hidden="true" />
            {copied ? 'Link Copied!' : 'Copy Shareable Link'}
          </button>
        </div>
      </div>

      {/* Grid cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          icon={<Wine className="w-5 h-5" aria-hidden="true" />}
          title="Wine"
          primary={`${results.wineBottles} bottles`}
          primaryLabel={`≈ ${results.wineCases} cases (12/case) · $${results.wineCost}`}
          lines={[
            { label: 'Red wine', value: `${results.redWineBottles} bottles` },
            { label: 'White / Rosé', value: `${results.whiteWineBottles} bottles` },
          ]}
        />
        <StatCard
          icon={<span className="text-lg" role="img" aria-label="Beer">🍺</span>}
          title="Beer"
          primary={`${results.beerServings} cans/bottles`}
          primaryLabel={`≈ ${results.beerCases} cases (24/case) · $${results.beerCost}`}
          lines={[
            { label: 'Keg equivalent', value: `${Math.ceil(results.beerServings / 165)} half-barrel keg(s)` },
          ]}
        />
        <StatCard
          icon={<Martini className="w-5 h-5" aria-hidden="true" />}
          title="Spirits"
          primary={`${results.spiritBottles} bottles`}
          primaryLabel={`750ml bottles · $${results.spiritCost}`}
          lines={[
            { label: 'Vodka', value: `${Math.ceil(results.spiritBottles * 0.25)} btl` },
            { label: 'Tequila', value: `${Math.ceil(results.spiritBottles * 0.2)} btl` },
            { label: 'Bourbon / Whiskey', value: `${Math.ceil(results.spiritBottles * 0.25)} btl` },
            { label: 'Gin & Rum', value: `${Math.ceil(results.spiritBottles * 0.3)} btl` },
          ]}
        />
        <StatCard
          icon={<span className="text-lg" role="img" aria-label="Champagne">🥂</span>}
          title="Champagne Toast"
          primary={`${results.champagneBottles} bottles`}
          primaryLabel={results.champagneBottles ? `$${results.champagneCost} estimated` : 'Toast disabled'}
          lines={[
            { label: 'Pours (4oz)', value: `${results.champagneBottles * 6}` },
          ]}
        />
        <StatCard
          icon={<span className="text-lg" role="img" aria-label="Mixers">🥤</span>}
          title="Mixers & Soda"
          primary={`${results.mixerLiters} L`}
          primaryLabel="Cola, club soda, tonic, cranberry, lime juice"
          lines={[
            { label: 'Per liquor bottle', value: '3 L mixers' },
          ]}
        />
        <StatCard
          icon={<Snowflake className="w-5 h-5" aria-hidden="true" />}
          title="Ice Estimate"
          primary={`${results.iceLbs} lbs`}
          primaryLabel={`≈ ${results.iceBags} × 10lb bags`}
          lines={[
            { label: 'Per guest', value: '1.5-2 lbs' },
          ]}
        />
      </div>
    </div>
  )
}
